// Common
import { type HttpContext } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { ContainerDndService } from '@modules/common/services/container-dnd.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';

// Services
import { FilesService } from '@modules/files/services/files.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { DragData, dragDataTypeAllowed, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/files/types/cross-workspace-drop-text';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { VirtualFolder } from '@modules/files/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';

// Rx
import { type Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// Components
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { mailboxTenantContext, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { Team } from '@modules/settings/types/team';
import { isStitchItemSummary } from '@sharedlib/dtos/dto/StitchItemSummaryDto';

@Component({
  selector: 'app-side-bar-folders-tree',
  templateUrl: './side-bar-folders-tree.component.html',
  styleUrls: ['./side-bar-folders-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarFoldersTreeComponent
  extends BaseSidebarContainersTreeComponent<Folder, File, FoldersFilters, VirtualFolder>
  implements OnInit
{
  @Input() public tenantId: string | null | undefined;

  selfDragDataTypes = [DragDataTypes.file, DragDataTypes.folder];
  containerDragDataType = DragDataTypes.folder;
  application = Application.files;
  treeStateKey = StateKey.filesSidebarFoldersTree;
  private readonly copyDropService = inject(ContainerDndService);
  private readonly copyStitchService = inject(StitchService);
  private readonly crossWorkspaceAlertService = inject(AlertService);
  private readonly filesTenantScope = inject(FilesTenantScopeService, { optional: true });
  private readonly filesSearch = inject(AdvancedSearchService);
  /** The rows and selection last confirmed, and that confirm read (cancelled by newer rows or another selection). */
  private selectionCheck?: { rows: Folder[]; ids: string; read: Subscription };

  public dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.folder &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type);

  constructor(
    private foldersService: FoldersService,
    private filesService: FilesService,
  ) {
    super(foldersService);
  }

  protected override requestContext(): HttpContext | undefined {
    if (this.tenantId === undefined) {
      return undefined;
    }
    const context = mailboxTenantContext(this.tenantId);
    return this.tenantId ? context.set(OWNING_SPACE_ID, this.tenantId) : context;
  }

  public override openItem(item: Folder): void {
    this.selectOwnFolderTenant();
    super.openItem(item);
  }

  protected override beforeContainerSelectionApplied(): void {
    this.selectOwnFolderTenant();
  }

  /**
   * Drops an open folder that belongs to another workspace — one left open in team A survives the switch to
   * team B, because the selection is kept per panel, not per workspace (#2290 MJ-01; mail's #1065 ROW 6, notes'
   * #2034). Only the tree that owns Files' current workspace drops: the personal rail's single tree, or the team
   * rail's tree whose tenant is Files' effective one.
   *
   * The rows are read WITH the rail's filters (Flagged, Pinned, Stitch, the date window), so a live folder those
   * filters hide is missing from them too. A selection none of the rows holds is therefore confirmed first by one
   * owner-scoped read of exactly those ids with no rail filter, and dropped only if that read answers none of them
   * while the selection is still those ids — the notes tree's rule (review-codex-2052 F1).
   */
  protected override onItemsReceived(): void {
    if (this.tenantId === undefined || (this.tenantId ?? null) === this.filesTenantScope?.effectiveTenantId()) {
      this.confirmSelectionGone();
    }
  }

  private confirmSelectionGone(): void {
    const ids = this.selectedFolderIds();
    const key = ids.join();
    // The base re-runs this hook on every search-state change: the same rows and selection are confirmed once.
    if (this.selectionCheck?.rows === this.items && this.selectionCheck.ids === key) {
      return;
    }
    // A newer rows load (or another selection) cancels a confirm in flight.
    this.selectionCheck?.read.unsubscribe();
    this.selectionCheck = undefined;
    if (ids.length === 0 || ids.some((id) => this.items.some((item) => item.id === id))) {
      return;
    }

    const requestContext = this.requestContext();
    const rows = this.items;
    const read = this.foldersService
      .findAll(new FoldersFilters({ ids, deleted: false }), requestContext ? { requestContext } : undefined)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((found) => {
        if (found.some((folder) => ids.includes(folder.id)) || this.selectedFolderIds().join() !== key) {
          return;
        }
        this.dropForeignContainerSelection();
      });
    this.selectionCheck = { rows, ids: key, read };
  }

  private selectedFolderIds(): string[] {
    return this.filesSearch.getStateSync()[this.application].containersIds;
  }

  private selectOwnFolderTenant(): void {
    this.stateService.setTeam(this.tenantId ? new Team({ id: this.tenantId }) : undefined);
  }

  /**
   * Actions
   */

  /**
   * Keeps the cross-workspace dialog honest while preserving every same-workspace action.
   *
   * `Copy` is disabled with the rest: the backend has no two-tenant duplicate for files, so both
   * `POST /files/duplicate` and `POST /folders/duplicate` look the source up under the DESTINATION
   * `space_id` only and cannot find the item the dialog hands them. Round 1 shipped it live. An action
   * the app cannot perform is shown disabled with a reason, never live and never faked (Pavel,
   * 2026-09-09). Each replacement button drops `click` entirely, so no request can fire even if the
   * disabled attribute is lost.
   */
  protected override getDndModalButtons(dragData: DragData, item: Stitch): AlertButtonConfig[] {
    const buttons = super.getDndModalButtons(dragData, item);
    if (!this.isCrossWorkspaceDrop(dragData)) {
      return buttons;
    }

    return buttons.map((button) => {
      if (button.title === 'Copy') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip:
            dragData.type === DragDataTypes.folder
              ? CROSS_WORKSPACE_DROP_TEXT.folderCopyTooltip
              : CROSS_WORKSPACE_DROP_TEXT.fileCopyTooltip,
        };
      }
      if (button.title === 'Move') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        };
      }
      if (button.title === 'Stitch') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        };
      }
      return button;
    });
  }

  public override dndDrop(dragData: DragData, item: Stitch): void {
    if (dragData.type === DragDataTypes.file) {
      const owner = getStringField(item, 'accountId') ?? this.tenantId;
      const owners = new Set(dragData.data.map((source) => getStringField(source, 'accountId')));
      if (!dragData.data.length || owners.size !== 1 || !owners.has(owner)) {
        this.copyDropService.handleChildItemDrop(
          new Folder({ id: item.id, title: item.title, accountId: owner }),
          dragData,
          {
            containerType: DragDataTypes.folder,
            childItemType: DragDataTypes.file,
            childService: this.filesService,
            containerIdField: 'folderId',
            crossTenantCopySupported: true,
            cancelled: this.alive,
            isActive: () => !this.alive.isStopped,
            appStateService: this.stateService,
          },
          this.copyStitchService,
          this.crossWorkspaceAlertService,
        );
        return;
      }
    }
    // Only the two types this tree owns actions for reach the honest dialog. Any other draggable — a
    // task, note or event dragged out of a file's linked-info list — stays on the shared tree, whose
    // stitch path already answers for it; round 1 handed it this dialog instead, and with it a LIVE
    // Copy calling `handleDuplicate`, a file/folder if-else-if with no `else` (notes r18 F1).
    const ownsCrossWorkspaceDialog = dragData.type === DragDataTypes.file || dragData.type === DragDataTypes.folder;

    if (!ownsCrossWorkspaceDialog || !this.isCrossWorkspaceDrop(dragData)) {
      super.dndDrop(dragData, item);
      return;
    }

    this.crossWorkspaceAlertService.show({
      title: 'Copy, Move or Stitch?',
      body: 'What do we do with the dropped item?',
      note:
        dragData.type === DragDataTypes.folder
          ? CROSS_WORKSPACE_DROP_TEXT.folderNote
          : CROSS_WORKSPACE_DROP_TEXT.fileNote,
      rightButtons: this.getDndModalButtons(dragData, item),
    });
  }

  public handleMove(dragData: DragData, item: Folder) {
    const message = 'Successfully moved to ' + item.title;

    if (dragData.type === DragDataTypes.file) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.filesService, dragData.data, () =>
        this.filesService.bunchUpdate({ ids }, { folderId: item.id }, { message }),
      );
    } else if (dragData.type === DragDataTypes.folder) {
      this.foldersService.bunchUpdate({ ids: this.getIds(dragData.data) }, { parentId: item.id }, { message });
    }
  }

  public handleDuplicate(dragData: DragData, folder: Folder) {
    const tenantItems = dragData.data.filter(isStitchItemSummary);
    const config = { tenantItems, requestContext: this.requestContext() };
    if (dragData.type === DragDataTypes.file) {
      this.filesService.duplicate(tenantItems, folder.id, config).pipe(take(1)).subscribe();
    } else if (dragData.type === DragDataTypes.folder) {
      this.foldersService.duplicate(tenantItems, undefined, config).pipe(take(1)).subscribe();
    }
  }

  private isCrossWorkspaceDrop(dragData: DragData): boolean {
    const sourceTenantId = getStringField(dragData.data[0], 'accountId');
    const targetTenantId = this.tenantId ?? this.spaceContextStore.contextId();
    return sourceTenantId !== undefined && targetTenantId !== undefined && sourceTenantId !== targetTenantId;
  }
}
